import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Mail, Lock, ArrowLeft, LogIn } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';

const Login = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    const result = await login(formData.email, formData.password);

    if (result.success) {
      toast.success(`Welcome back, ${result.user.name}!`);
      navigate('/dashboard');
    } else {
      toast.error(result.error);
    }

    setLoading(false);
  };

  return (
    <div className="min-h-screen pt-24 pb-16 flex items-center justify-center" style={{ background: 'var(--bg-dark)' }}>
      {/* Background Accents */}
      <div className="fixed top-1/3 left-0 w-[300px] h-[300px] bg-[#3B82F6]/10 rounded-full blur-[120px] pointer-events-none"></div>
      <div className="fixed bottom-1/3 right-0 w-[300px] h-[300px] bg-[#8B5CF6]/10 rounded-full blur-[120px] pointer-events-none"></div>

      <div className="relative w-full max-w-md mx-auto px-4 sm:px-6">
        <button
          data-testid="back-btn"
          onClick={() => navigate('/')}
          className="flex items-center gap-2 text-[#3B82F6] hover:text-white transition-colors duration-300 mb-6"
        >
          <ArrowLeft size={20} />
          <span>Back to Home</span>
        </button>

        <div className="bg-[#12121A]/80 backdrop-blur-xl border border-white/10 rounded-2xl p-6 sm:p-8 md:p-12">
          <div className="text-center mb-8">
            <div className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4" style={{ background: 'linear-gradient(135deg, #3B82F6, #8B5CF6)' }}>
              <LogIn size={32} className="text-white" />
            </div>
            <h1 className="text-3xl md:text-4xl font-bold text-white mb-2 whitespace-nowrap">Welcome Back</h1>
            <p className="text-white/50 whitespace-nowrap">Login to access your dashboard</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Email */}
            <div>
              <label htmlFor="email" className="flex items-center gap-2 text-white/70 mb-2 text-sm font-medium">
                <Mail size={16} className="text-[#3B82F6]" />
                Email Address
              </label>
              <input
                type="email"
                id="email"
                name="email"
                data-testid="login-email"
                value={formData.email}
                onChange={handleChange}
                required
                className="w-full bg-[#0A0A0F] border border-white/10 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-[#3B82F6] focus:ring-1 focus:ring-[#3B82F6]/50 transition-all duration-300"
                placeholder="john@example.com"
              />
            </div>

            {/* Password */}
            <div>
              <label htmlFor="password" className="flex items-center gap-2 text-white/70 mb-2 text-sm font-medium">
                <Lock size={16} className="text-[#8B5CF6]" />
                Password
              </label>
              <input
                type="password"
                id="password"
                name="password"
                data-testid="login-password"
                value={formData.password}
                onChange={handleChange}
                required
                className="w-full bg-[#0A0A0F] border border-white/10 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-[#8B5CF6] focus:ring-1 focus:ring-[#8B5CF6]/50 transition-all duration-300"
                placeholder="Enter your password"
              />
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              data-testid="login-submit"
              disabled={loading}
              className="w-full py-4 text-lg font-semibold text-white rounded-xl transition-all duration-300 hover:scale-[1.02] hover:shadow-xl hover:shadow-[#3B82F6]/25 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-3"
              style={{ background: 'linear-gradient(135deg, #3B82F6, #8B5CF6)' }}
            >
              {loading ? (
                <>
                  <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                  Logging in...
                </>
              ) : (
                <>
                  <LogIn size={20} />
                  Login
                </>
              )}
            </button>

            {/* Signup Link */}
            <p className="text-center text-white/50">
              Don't have an account?{' '}
              <Link to="/signup" className="text-[#3B82F6] hover:text-[#60A5FA] font-medium transition-colors duration-300">
                Sign up here
              </Link>
            </p>
          </form>
        </div>
      </div>
    </div>
  );
};

export default Login;
